import { ElementType } from "react";
import { Typography, TypographyProps } from "@mui/material";

const headingFontSizeMap = {
    "6xl": {
        fontSize: "6xl",
        fontWeight: "heavy",
        letterSpacing: "1.5px",
        lineHeight: 1.15,
    },
    "5xl": {
        fontSize: "5xl",
        fontWeight: "heavy",
        letterSpacing: "1.5px",
        lineHeight: 1.15,
    },
    "4xl": {
        fontSize: "4xl",
        fontWeight: "heavy",
        letterSpacing: "0",
        lineHeight: 1.25,
    },
    "3xl": {
        fontSize: "3xl",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.25,
    },
    "2xl": {
        fontSize: "2xl",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
    xl: {
        fontSize: "xl",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.6,
    },
    lg: {
        fontSize: "lg",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
    md: {
        fontSize: "md",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
    sm: {
        fontSize: "sm",
        fontWeight: "semibold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
};

type IFontSize = keyof typeof headingFontSizeMap;

interface IFontSizeResponsiveMap {
    xs?: IFontSize;
    sm?: IFontSize;
    md?: IFontSize;
    lg?: IFontSize;
}

export const buildResponsiveHeaderFont = (fontSize: IFontSize | IFontSizeResponsiveMap) => {
    if (typeof fontSize === "string") {
        return headingFontSizeMap[fontSize];
    }

    const fontSx = {
        sx: {
            fontSize: {} as Record<string, string>,
            fontWeight: {} as Record<string, string>,
            letterSpacing: {} as Record<string, string>,
            lineHeight: {} as Record<string, number>,
        },
    };

    Object.entries(fontSize).forEach(([breakpoint, size]) => {
        const fontSizeEntry = headingFontSizeMap[size as IFontSize];

        if (size && fontSizeEntry) {
            const { fontSize, fontWeight, letterSpacing, lineHeight } = fontSizeEntry;
            fontSx.sx.fontSize[breakpoint] = fontSize;
            fontSx.sx.fontWeight[breakpoint] = fontWeight;
            fontSx.sx.letterSpacing[breakpoint] = letterSpacing;
            fontSx.sx.lineHeight[breakpoint] = lineHeight;
        }
    });

    return fontSx;
};

export interface IWfHeadingProps extends Omit<TypographyProps, "fontSize"> {
    fontSize: IFontSize | IFontSizeResponsiveMap;
    component: ElementType;
}

export const WfHeading = ({ fontSize, ...props }: IWfHeadingProps) => {
    const fontValues = buildResponsiveHeaderFont(fontSize);

    return <Typography {...fontValues} {...props} />;
};
